/**
 * <LoginForm> — shared tenant/platform login UI.
 *
 * Consumed by:
 *   - hr-admin `/login` (tenantSlug from resolveTenant, showForgotLink=true)
 *   - employee-web `/login` (same)
 *   - link-admin `/login` (Plan 07; uses `<PlatformLoginForm>` wrapper that
 *     calls `useAuth().platformLogin` instead of `login`; reuses this JSX
 *     via a prop or a sibling component — executor's call in Plan 07)
 *
 * Handles:
 *   - Zod validation via `loginSchema` (UI-SPEC §Form Validation Contract).
 *   - Tagged-union success:
 *       - `kind: "success"` → calls `onSuccess(snapshot)`
 *       - `kind: "otp-required"` → calls `onOtpRequired({ otpSessionToken, ttl, channel })`
 *       - `kind: "totp-required"` → calls `onTotpRequired({ totpSessionToken, ttl })`
 *       - `kind: "workspace-required"` (only with `workspaceSelection`) → calls
 *         `onWorkspaceRequired({ workspaceToken, ttl, workspaces })`
 *   - Error branching (ts-pattern): AUTH_BAD_CREDENTIALS, TENANT_NOT_FOUND,
 *     TENANT_SUSPENDED, ACCOUNT_LOCKED → specific i18n keys; anything else
 *     (including network failures that throw TypeError) → auth.error.network.
 *   - Focus on the identifier field on mount.
 *
 * Inputs: the identifier takes an email OR a username (`<PREFIX>-<employee
 * number>`), so it is `type="text"` with an explicit `bidi="ltr"` and no
 * auto-capitalisation or lower-casing (the number is matched exactly);
 * password is `type="password"`, also `bidi="ltr"`.
 */
import { useEffect, useRef, useState, type JSX } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { match, P } from "ts-pattern";
import { Eye, EyeOff } from "lucide-react";
import { Alert, Button, Checkbox, Form, FormField, Icon, Input } from "@linkhr/design-system";
import { loginSchema, type LoginSchema } from "../schemas/authSchemas.js";
import type { AuthSnapshot, Audience } from "../types.js";
import { useAuth } from "../react/useAuth.js";
import {
  loginWithAudienceFallback,
  type LoginResult,
  type WorkspaceItem,
} from "../actions/login.js";
import { ADMIN_AUDIENCE_ERROR_CODE, AdminAudienceNotice } from "./AdminAudienceNotice.js";
import { accountingRefusalKey, isAccountingOnlyRefusal } from "../errors/authErrorCodes.js";
import { accountingAppUrl } from "../constants/appPaths.js";
import { useAuthAudience } from "../react/AuthProvider.js";
import { GoogleSignInButton } from "./GoogleSignInButton.js";
import { MicrosoftSignInButton } from "./MicrosoftSignInButton.js";

export interface LoginFormProps {
  /** Resolved tenant slug (empty string for platform — see PlatformLoginForm). */
  tenantSlug: string;
  apiBaseUrl: string;
  /**
   * Called when the server returns `kind: "success"` — snapshot already
   * committed. `snapshot.mustChangePassword` → go to `/auth/change-password`,
   * not the redirect target.
   */
  onSuccess: (snapshot: AuthSnapshot) => void;
  /** Called when the server returns `kind: "otp-required"`. */
  onOtpRequired: (otp: {
    otpSessionToken: string;
    ttl: number;
    channel: "email" | "sms";
    maskedEmail: string;
  }) => void;
  /** Called when the server returns `kind: "totp-required"` (authenticator app). */
  onTotpRequired: (totp: { totpSessionToken: string; ttl: number }) => void;
  /**
   * Offer a workspace choice when the address belongs to several workspaces.
   * Off by default: the server then refuses such an address instead.
   * @default false
   */
  workspaceSelection?: boolean;
  /** Called with the choice when `workspaceSelection` is on and the server asks for one. */
  onWorkspaceRequired?: (choice: {
    workspaceToken: string;
    ttl: number;
    workspaces: WorkspaceItem[];
  }) => void;
  /** Show "Forgot password?" ghost link; false for platform login. @default true */
  showForgotLink?: boolean;
  /** Custom heading — default uses `auth.login.heading`. */
  heading?: string;
  /**
   * Suppress the form's internal `<h1>` entirely. Used when the surrounding
   * page (e.g. `<LoginPage>`) already renders its own canonical title +
   * subtitle inside the card (Phase 05.2 Plan 09). The form keeps its
   * `aria-labelledby` wiring; the page-level heading owns `id="login-heading"`
   * via its own DOM, so a11y survives via the visible heading on the page.
   */
  hideHeading?: boolean;
  /** Optional forgot-password navigation callback — apps wire to TanStack Router. */
  onForgotPassword?: () => void;
  /**
   * Audience to sign in with. Omit — as all three per-audience apps do — and
   * the app's own audience (`createAppRoot({ aud })`) is used.
   */
  audience?: Audience;
  /**
   * The backend refused this audience (`AUTH_ADMIN_AUDIENCE_NOT_PERMITTED`).
   * Return the audience to retry the SAME credentials with, or `null` to show
   * the "use the employee app" notice instead.
   *
   * This is how the global login learns what it cannot know up front: it tries
   * `admin`, and a refusal — which arrives before any MFA step, so nothing is
   * half-started — sends it round again as `mobile`. The retry runs inside the
   * same submit, so the user sees one pending state, never two forms.
   */
  onAudienceRefused?: (attempted: Audience) => Audience | null;
  /**
   * Google sign-in web client id. Set → a "Continue with Google" button under
   * the form; its ID token goes through the same result handling, audience
   * retry and error mapping as the password. Absent → no button.
   */
  googleClientId?: string | undefined;
  /**
   * Microsoft (Entra) application id. Set → a "Sign in with Microsoft" button
   * under Google's, handled exactly like it. Absent → no button.
   */
  microsoftClientId?: string | undefined;
  /**
   * `onAudienceRefused` for a provider ID token (Google, Microsoft), when
   * re-sending the same token needs its own policy. Omit → `onAudienceRefused`
   * applies to every proof.
   */
  onIdTokenAudienceRefused?: (attempted: Audience) => Audience | null;
  /**
   * Called right before every request with the audience it is about to use —
   * the first try and each retry alike — so a caller that retargets
   * audience-scoped plumbing is never left on a previous attempt's audience.
   */
  onAttempt?: (audience: Audience) => void;
}

/** How the person proved who they are. */
type Proof = "password" | "google" | "microsoft";

/** The one message a credential refusal shows, per proof. */
const PROOF_FAILED_KEY: Record<Proof, string> = {
  password: "login.error.invalid_identifier_or_password",
  google: "login.error.google_failed",
  microsoft: "login.error.microsoft_failed",
};

/**
 * Mask an email for OTP sub-copy display: `ada@example.com` → `a**@example.com`.
 * Used only when the server doesn't supply `maskedEmail` in the CC-03 payload
 * (forward-compat — current backend shape stops at `{ttl, channel}`). Callers
 * pass only a typed address; a username or a provider sign-in has none.
 */
function maskEmail(email: string): string {
  const [local = "", domain = ""] = email.split("@");
  if (local.length <= 1 || domain === "") return email;
  return `${local[0] ?? ""}${"*".repeat(Math.max(local.length - 1, 2))}@${domain}`;
}

export function LoginForm({
  tenantSlug,
  apiBaseUrl,
  onSuccess,
  onOtpRequired,
  onTotpRequired,
  workspaceSelection = false,
  onWorkspaceRequired,
  showForgotLink = true,
  heading,
  hideHeading = false,
  onForgotPassword,
  audience,
  onAudienceRefused,
  onIdTokenAudienceRefused,
  onAttempt,
  googleClientId,
  microsoftClientId,
}: LoginFormProps): JSX.Element {
  const { t } = useTranslation("auth");
  const auth = useAuth();
  const [serverError, setServerError] = useState<{
    key: string;
    values?: Record<string, string | number>;
    /** The server's pre-localized title, shown instead of `key` when present. */
    text?: string;
  } | null>(null);
  // be-788 / CC-431: AUTH_ADMIN_AUDIENCE_NOT_PERMITTED renders the SERVER's
  // pre-localized title + an employee-app link, never bad-credentials copy.
  const [audienceError, setAudienceError] = useState<string | null>(null);
  // PRODUCT_LOCKED on an HR sign-in: the account opens Link Accounting only.
  const [accountingOnly, setAccountingOnly] = useState(false);
  const appAudience = useAuthAudience();
  // Show/hide the password (typo guard on a field the user can't read). A real
  // button at the input's end edge, icon-only with an accessible name.
  const [showPassword, setShowPassword] = useState(false);

  const methods = useForm<LoginSchema>({
    resolver: zodResolver(loginSchema),
    defaultValues: { identifier: "", password: "", rememberMe: true },
    mode: "onSubmit",
  });

  // Focus the identifier field on mount.
  const identifierRef = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    identifierRef.current?.focus();
  }, []);

  // Provider sign-in in flight — keeps the password submit from racing it.
  const [providerPending, setProviderPending] = useState(false);
  // GIS failed to load: drop Google (and the divider, if nothing else is left).
  const [googleUnavailable, setGoogleUnavailable] = useState(false);
  const showGoogle = googleClientId !== undefined && googleClientId !== "" && !googleUnavailable;
  const showMicrosoft = microsoftClientId !== undefined && microsoftClientId !== "";

  /**
   * One sign-in attempt, whatever the proof: `call` sends it for an audience.
   * A refused audience is retried with the SAME proof via
   * `loginWithAudienceFallback` (the caller's `onAudienceRefused` decides
   * where); everything else lands in the shared result/error handling below.
   */
  const attempt = async (
    call: (aud: Audience | undefined) => Promise<LoginResult>,
    { identifier, via }: { identifier: string; via: Proof },
  ): Promise<void> => {
    try {
      const result = await loginWithAudienceFallback(
        (aud) => {
          if (aud !== undefined) onAttempt?.(aud);
          return call(aud);
        },
        audience,
        via === "password" ? onAudienceRefused : (onIdTokenAudienceRefused ?? onAudienceRefused),
      );
      if (result.kind === "otp-required") {
        onOtpRequired({
          otpSessionToken: result.otpSessionToken,
          ttl: result.ttl,
          channel: result.channel,
          // The code goes to the account's email, which a username (or a
          // provider sign-in) doesn't show us: name it generically.
          maskedEmail: identifier.includes("@") ? maskEmail(identifier) : t("otp.your_email"),
        });
        return;
      }
      if (result.kind === "totp-required") {
        // Authenticator-app challenge — hand the short-lived session token to
        // the TOTP step (there is no access token on a totp_required response).
        onTotpRequired({
          totpSessionToken: result.totpSessionToken,
          ttl: result.ttl,
        });
        return;
      }
      if (result.kind === "workspace-required") {
        // Only asked for with `workspaceSelection`; a caller without a picker
        // fails closed to the generic error.
        if (onWorkspaceRequired === undefined) {
          setServerError({ key: "error.network" });
          return;
        }
        onWorkspaceRequired({
          workspaceToken: result.workspaceToken,
          ttl: result.ttl,
          workspaces: result.workspaces,
        });
        return;
      }
      onSuccess(result.snapshot);
    } catch (err) {
      const apiErr =
        err !== null && typeof err === "object" ? (err as Record<string, unknown>) : {};
      const errorCode = String((apiErr.errorCode as string | undefined) ?? "");
      const status = Number((apiErr.status as number | undefined) ?? 0);
      const retryAfter = apiErr.retryAfterSeconds as number | undefined;
      // ApiError sets message = the RFC 7807 title, so `title` is the server's
      // pre-localized copy at this seam. Fall back to the core-owned session
      // key only if a proxy stripped the body.
      const serverTitle = typeof apiErr.title === "string" ? apiErr.title.trim() : "";
      // Branch the audience 403 BEFORE any generic mapping: the password was
      // right, this surface just isn't theirs — never bad-credentials copy.
      // Only reaches here once `loginWithAudienceFallback` has run out of
      // surfaces to retry.
      if (errorCode === ADMIN_AUDIENCE_ERROR_CODE) {
        setAudienceError(serverTitle !== "" ? serverTitle : t("session.admin_access_revoked"));
        return;
      }
      if (isAccountingOnlyRefusal(errorCode, audience ?? appAudience)) {
        setAccountingOnly(true);
        return;
      }
      const accountingRefusal = accountingRefusalKey(errorCode);
      if (accountingRefusal !== null) {
        setServerError({ key: accountingRefusal });
        return;
      }
      // Only reached once the password is proven, so the server's copy (ask HR
      // for a new temporary password / no email for the emailed code) reveals
      // nothing about whether an account exists.
      if (
        errorCode === "AUTH_TEMP_PASSWORD_EXPIRED" ||
        errorCode === "AUTH_SECOND_FACTOR_UNAVAILABLE"
      ) {
        const key =
          errorCode === "AUTH_TEMP_PASSWORD_EXPIRED"
            ? "login.error.temp_password_expired"
            : "login.error.second_factor_unavailable";
        setServerError(serverTitle !== "" ? { key, text: serverTitle } : { key });
        return;
      }
      // Keys are namespace-relative (useTranslation("auth") above) — do NOT prefix with "auth.".
      // 429 from the gateway/backend often returns an opaque body with no
      // errorCode, so we also branch on HTTP status to catch rate limiting
      // even when the backend doesn't mint a stable error code.
      if (
        errorCode === "AUTH_RATE_LIMITED" ||
        errorCode === "RATE_LIMIT_EXCEEDED" ||
        status === 429
      ) {
        const key =
          retryAfter !== undefined && retryAfter > 0
            ? "login.error.rate_limited_retry"
            : "login.error.rate_limited";
        setServerError({ key, values: retryAfter !== undefined ? { seconds: retryAfter } : {} });
        return;
      }
      const key = match(errorCode)
        // Anti-enumeration: collapse every credential-failure state — bad
        // password, unknown email, pending-invite, disabled — to ONE neutral
        // message so the login screen never reveals whether an account exists
        // or its state. (Backend is collapsing these pre-auth too; this is the
        // client-side belt that holds under both the current and fixed backend.)
        .with(
          P.union("AUTH_BAD_CREDENTIALS", "AUTH_USER_PENDING_INVITE", "AUTH_USER_DISABLED"),
          // A provider: one generic "couldn't sign you in" — never why (the
          // backend only accepts some inboxes per provider; that stays unsaid).
          () => PROOF_FAILED_KEY[via],
        )
        .with(
          P.union(
            "TENANT_NOT_FOUND",
            "TENANT_SUSPENDED",
            "SUBSCRIPTION_PURGED",
            "SUBSCRIPTION_NOT_FOUND",
          ),
          () => "login.error.tenant_unavailable",
        )
        .with("ACCOUNT_LOCKED", () => "login.error.account_locked")
        // An outsider's access end date has passed. Only returned once the
        // password is proven, so naming it reveals nothing about the account.
        .with("AUTH_ACCOUNT_EXPIRED", () => "login.error.account_expired")
        .otherwise(() => "error.network");
      setServerError({ key });
    }
  };

  const onSubmit = methods.handleSubmit(async (values) => {
    setServerError(null);
    setAudienceError(null);
    setAccountingOnly(false);
    await attempt(
      (aud) =>
        auth.login({
          identifier: values.identifier,
          password: values.password,
          tenantSlug,
          apiBaseUrl,
          rememberMe: values.rememberMe,
          ...(workspaceSelection ? { workspaceSelection: true } : {}),
          ...(aud !== undefined ? { audience: aud } : {}),
        }),
      { identifier: values.identifier, via: "password" },
    );
  });

  const onIdToken = async (via: "google" | "microsoft", idToken: string): Promise<void> => {
    setServerError(null);
    setAudienceError(null);
    setAccountingOnly(false);
    setProviderPending(true);
    const send = via === "google" ? auth.googleLogin : auth.microsoftLogin;
    try {
      await attempt(
        (aud) =>
          send({
            idToken,
            apiBaseUrl,
            rememberMe: methods.getValues("rememberMe"),
            ...(aud !== undefined ? { audience: aud } : {}),
          }),
        { identifier: "", via },
      );
    } finally {
      setProviderPending(false);
    }
  };

  const isSubmitting = methods.formState.isSubmitting || providerPending;

  return (
    <FormProvider {...methods}>
      <Form onSubmit={onSubmit} aria-labelledby="login-heading">
        {!hideHeading && (
          <h1 id="login-heading" className="text-heading text-default">
            {heading ?? t("login.heading")}
          </h1>
        )}

        {serverError !== null && (
          <Alert
            variant="danger"
            description={
              serverError.text ??
              (serverError.values !== undefined
                ? t(serverError.key, serverError.values)
                : t(serverError.key))
            }
          />
        )}

        {audienceError !== null && <AdminAudienceNotice serverTitle={audienceError} />}
        {accountingOnly && (
          <AdminAudienceNotice
            serverTitle={t("login.error.accounting_only")}
            href={`${accountingAppUrl()}login`}
            linkLabel={t("login.open_accounting")}
          />
        )}

        <FormField<LoginSchema, "identifier">
          name="identifier"
          label={t("login.identifier_label")}
          required
          render={(field) => (
            <Input
              id={field.id}
              name={field.name}
              type="text"
              bidi="ltr"
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              ref={(node) => {
                field.ref(node);
                identifierRef.current = node;
              }}
              invalid={field.invalid}
              aria-describedby={field["aria-describedby"]}
              aria-invalid={field["aria-invalid"]}
              aria-required={field["aria-required"]}
            />
          )}
        />

        <FormField<LoginSchema, "password">
          name="password"
          label={t("login.password_label")}
          required
          render={(field) => (
            /* `dir="ltr"` on the wrapper, not just the input: the field's
               content is Latin by contract, so the input reserves its space
               with `pe-xl` on the physical right. An overlay positioned by the
               Arabic shell's direction would sit on the left — on top of the
               first characters the user types. Anchoring both to the same
               direction keeps the toggle beside the text in either language. */
            <div className="relative" dir="ltr">
              <Input
                id={field.id}
                name={field.name}
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                bidi="ltr"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                ref={field.ref}
                invalid={field.invalid}
                aria-describedby={field["aria-describedby"]}
                aria-invalid={field["aria-invalid"]}
                aria-required={field["aria-required"]}
                className="pe-xl"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                aria-pressed={showPassword}
                aria-label={showPassword ? t("login.hide_password") : t("login.show_password")}
                className="absolute end-sm top-1/2 -translate-y-1/2 rounded-sm text-muted hover:text-default focus-visible:focus-ring"
              >
                <Icon as={showPassword ? EyeOff : Eye} size={16} aria-label="" />
              </button>
            </div>
          )}
        />

        <label className="flex items-center gap-xs text-label text-default cursor-pointer">
          <Checkbox
            id="login-remember-me"
            checked={methods.watch("rememberMe") ?? true}
            onCheckedChange={(v) => methods.setValue("rememberMe", v === true)}
          />
          <span>{t("login.remember_me")}</span>
        </label>

        <Button type="submit" variant="primary" disabled={isSubmitting} className="w-full">
          {t("login.cta")}
        </Button>

        {(showGoogle || showMicrosoft) && (
          <div className="flex flex-col gap-md">
            <div className="flex items-center gap-sm text-muted text-sm" role="separator">
              <span className="h-px flex-1 bg-border-subtle" aria-hidden="true" />
              <span>{t("login.or")}</span>
              <span className="h-px flex-1 bg-border-subtle" aria-hidden="true" />
            </div>
            {showGoogle && (
              <GoogleSignInButton
                clientId={googleClientId}
                onCredential={(idToken) => void onIdToken("google", idToken)}
                onUnavailable={() => setGoogleUnavailable(true)}
              />
            )}
            {showMicrosoft && (
              <MicrosoftSignInButton
                clientId={microsoftClientId}
                disabled={isSubmitting}
                onIdToken={(idToken) => void onIdToken("microsoft", idToken)}
                onError={() => {
                  setAudienceError(null);
                  setAccountingOnly(false);
                  setServerError({ key: PROOF_FAILED_KEY.microsoft });
                }}
              />
            )}
          </div>
        )}

        {showForgotLink && onForgotPassword !== undefined && (
          <Button type="button" variant="ghost" size="sm" onClick={onForgotPassword}>
            {t("login.forgot_password")}
          </Button>
        )}
      </Form>
    </FormProvider>
  );
}
